import _ from 'lodash';
import memoizeOne from 'memoize-one';

export const workflowGridColumns = [
  { headerName: 'Step', field: 'step', rowDrag: true },
  { headerName: 'Sub Task Type', field: 'subTaskTypeName' },
  { headerName: 'Duration(in minutes)', field: 'duration' },
  { headerName: 'Team', field: 'team' },
  { headerName: 'Predecessor', field: 'predecessorSteps' },
  { headerName: 'Auto Resolve With', field: 'autoResolveDependency' },
  { headerName: 'Immediate After To', field: 'immediateNextToName' },
  { headerName: 'Same Assignee As', field: 'sameAssigneeAsName' },
  { headerName: 'Same Start Time As', field: 'sameStartTimeAsSteps' },
  { headerName: 'Form Section', field: 'formSectionName' },
  { headerName: 'Preferred Location Type', field: 'preferredLocationTypeName' },
  { headerName: 'Same Location As', field: 'sameLocationAsName' },
  { headerName: 'Actions', field: '', cellRenderer: 'actionsColumnRenderer' },
];

export const workFlowGridTemplateColumns = [
  { headerName: 'Step', field: 'step' },
  { headerName: 'Sub Task Type', field: 'subTaskTypeName' },
  { headerName: 'Stop', field: 'stopName' },
  { headerName: 'Asset', field: 'assetName' },
  { headerName: 'Duration(in minutes)', field: 'duration' },
  { headerName: 'Predecessor', field: 'predecessorSteps' },
  { headerName: 'Immediate After To', field: 'immediateNextToName' },
  { headerName: 'Same Start Time As', field: 'sameStartTimeAsSteps' },
  { headerName: 'Same Assignee As', field: 'sameAssigneeAsName' },
  { headerName: 'Form Section', field: 'formSectionName' },
];

export function convertWorkflowTableDataToDbWorkflow(workflowData, version) {
  const workflow = {};
  workflow.version = version;
  workflow.workflowSteps = workflowData.map((workflowRow) => ({
    taskTypeId: workflowRow.subTaskTypeId,
    dependsOn: workflowRow.predecessors,
    immediateNextTo: workflowRow.immediateNextTo,
    sameStartTimeAs: workflowRow.sameStartTimeAs,
    sameAssigneeAs: workflowRow.sameAssigneeAs || null,
    workflowStepId: workflowRow.uuid,
    formSectionId: workflowRow.formSectionId || null,
    preferredLocationTypeId: workflowRow.preferredLocationTypeId || null,
    sameLocationAs: workflowRow.sameLocationAs || null,
    autoResolveDependsOn: workflowRow.autoResolveDependsOn || [],
  }));
  return workflow;
}

export function convertDbWorkflowToWorkflowTableData(workflow) {
  const workflowSteps = workflow.workflowSteps.map((step) => ({
    subTaskTypeId: step.taskTypeId,
    predecessors: step.dependsOn || [],
    immediateNextTo: step.immediateNextTo || '',
    uuid: step.workflowStepId,
    sameAssigneeAs: step.sameAssigneeAs,
    sameStartTimeAs: step.sameStartTimeAs || [],
    formSectionId: step.formSectionId,
    preferredLocationTypeId: step.preferredLocationTypeId || null,
    assetId: step.assetId,
    sameLocationAs: step.sameLocationAs || null,
    autoResolveDependsOn: step.autoResolveDependsOn || [],
  }));
  return workflowSteps;
}

function getWorkflowStepAndSubTaskTypeName(workflowStepId, workflowUUIDMap, allTaskTypesIdMap) {
  if (!workflowStepId || !workflowUUIDMap[workflowStepId]) {
    return '';
  }
  return `${workflowUUIDMap[workflowStepId].step} - ${
    allTaskTypesIdMap[workflowUUIDMap[workflowStepId].subTaskTypeId].name
  }`;
}

export const getWorkflowTableData = memoizeOne(
  (
    workflowData,
    allTaskTypes,
    allTeams,
    formSections,
    templateAssetsInfoMap = {},
    preferredLocationTypeOptions = []
  ) => {
    const formattedWorkflowData = _.cloneDeep(workflowData);

    let step = 1;
    formattedWorkflowData.forEach((workflow) => {
      // eslint-disable-next-line no-plusplus,no-param-reassign
      workflow.step = step++;
    });

    const workflowUUIDMap = _.keyBy(formattedWorkflowData, 'uuid');
    const allTaskTypesIdMap = _.keyBy(allTaskTypes, 'id');
    const teamIdMap = _.keyBy(allTeams, 'id');
    const formSectionIdMap = _.keyBy(formSections, 'id');
    const preferredLocationTypeOptionDict = _.keyBy(preferredLocationTypeOptions, 'value');

    formattedWorkflowData.forEach((workflow) => {
      const subTaskType = allTaskTypesIdMap[workflow.subTaskTypeId];
      if (subTaskType) {
        workflow.subTaskTypeName = subTaskType.name;
        workflow.duration = subTaskType.duration;
        workflow.team = teamIdMap[subTaskType.teamId]?.name ?? '';
        workflow.sameAssigneeAsName = workflow.sameAssigneeAs
          ? getWorkflowStepAndSubTaskTypeName(workflow.sameAssigneeAs, workflowUUIDMap, allTaskTypesIdMap)
          : '';
        workflow.immediateNextToName = workflow.immediateNextTo
          ? getWorkflowStepAndSubTaskTypeName(workflow.immediateNextTo, workflowUUIDMap, allTaskTypesIdMap)
          : '';
        workflow.predecessors = workflow.predecessors.filter((workflowStepId) => workflowUUIDMap[workflowStepId]);

        workflow.predecessorSteps = workflow.predecessors
          .map((workflowStepId) =>
            getWorkflowStepAndSubTaskTypeName(workflowStepId, workflowUUIDMap, allTaskTypesIdMap)
          )
          .join(', ');
        workflow.sameStartTimeAsSteps = workflow.sameStartTimeAs
          .map((workflowStepId) =>
            getWorkflowStepAndSubTaskTypeName(workflowStepId, workflowUUIDMap, allTaskTypesIdMap)
          )
          .join(', ');
        workflow.autoResolveDependency = workflow.autoResolveDependsOn
          .map((workflowStepId) =>
            getWorkflowStepAndSubTaskTypeName(workflowStepId, workflowUUIDMap, allTaskTypesIdMap)
          )
          .join(', ');
        workflow.formSectionName = _.get(formSectionIdMap, `${workflow.formSectionId}.state.label`, '');
        workflow.preferredLocationTypeName = _.get(
          preferredLocationTypeOptionDict,
          `${workflow.preferredLocationTypeId}.text`
        );
        workflow.sameLocationAsName = workflow.sameLocationAs
          ? getWorkflowStepAndSubTaskTypeName(workflow.sameLocationAs, workflowUUIDMap, allTaskTypesIdMap)
          : '';
        if (workflow.assetId && templateAssetsInfoMap[workflow.assetId]) {
          workflow.assetName = templateAssetsInfoMap[workflow.assetId].assetName;
          workflow.stopName = templateAssetsInfoMap[workflow.assetId].stopName;
        }
      }
    });
    return formattedWorkflowData;
  }
);
